import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { BookOpen, Mail, Lock, User, Eye, EyeOff, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [isForgot, setIsForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotMethod, setForgotMethod] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: formData.email })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send reset email');
      }
      setForgotMethod(data.method || '');
      setForgotMessage(data.message || '');
      setForgotSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!isLogin) {
        // Registration validation
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (formData.password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
      }

      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const body = isLogin 
        ? { email: formData.email, password: formData.password }
        : { name: formData.name, email: formData.email, password: formData.password };

      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Authentication failed');
      }

      // Call the onLogin callback with user data
      if (onLogin) {
        onLogin(data);
      }
      
      // Redirect based on role
      if (data.role === 'admin') {
        navigate('/admin', { replace: true });
      } else {
        navigate('/portal', { replace: true });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Back to Home */}
        <Link 
          to="/" 
          className="inline-flex items-center text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        {/* Logo */}
        <div className="flex items-center justify-center mb-8">
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center">
            <BookOpen className="w-7 h-7 text-white" />
          </div>
          <span className="text-2xl font-bold text-white ml-3">Life Decoder</span>
        </div>

        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-8">
            {isForgot ? (
              <>
                <div className="text-center mb-6">
                  <h1 className="text-2xl font-bold text-white mb-2">Reset your password</h1>
                  <p className="text-gray-400">
                    {forgotSent
                      ? (forgotMethod === 'whatsapp'
                          ? "Check WhatsApp for your temporary password"
                          : "Need help recovering your account?")
                      : "Enter your email — we'll send a temporary password to your WhatsApp"}
                  </p>
                </div>

                {error && (
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <p className="text-red-400 text-sm text-center" data-testid="forgot-error">{error}</p>
                  </div>
                )}

                {forgotSent ? (
                  <div className="space-y-4">
                    <div className={`p-4 border rounded-lg ${forgotMethod === 'whatsapp' ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-amber-500/10 border-amber-500/20'}`}>
                      <p className={`text-sm text-center ${forgotMethod === 'whatsapp' ? 'text-emerald-300' : 'text-amber-200'}`} data-testid="forgot-success">
                        {forgotMessage || (forgotMethod === 'whatsapp'
                          ? <>A temporary password has been sent to your WhatsApp. Please use it to log in, then change your password from your Profile.</>
                          : <>If an account exists for <strong>{formData.email}</strong>, a temporary password has been sent to your WhatsApp.</>)}
                      </p>
                      {forgotMethod !== 'whatsapp' && (
                        <a
                          href="https://wa.me/60122493838"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 flex items-center justify-center gap-2 text-sm text-emerald-300 hover:text-emerald-200 underline"
                          data-testid="contact-admin-whatsapp"
                        >
                          Contact Admin on WhatsApp →
                        </a>
                      )}
                    </div>
                    <Button
                      type="button"
                      onClick={() => {
                        setIsForgot(false);
                        setForgotSent(false);
                        setForgotMethod('');
                        setForgotMessage('');
                        setError('');
                      }}
                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-5"
                      data-testid="back-to-login-btn"
                    >
                      Back to Sign In
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotSubmit} className="space-y-4" data-testid="forgot-password-form">
                    <div>
                      <Label htmlFor="email" className="text-gray-400">Email Address</Label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="you@example.com"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          data-testid="forgot-email-input"
                          className="pl-10 bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      data-testid="forgot-submit-btn"
                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-5"
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />
                          Sending...
                        </div>
                      ) : (
                        'Send to WhatsApp'
                      )}
                    </Button>

                    <button
                      type="button"
                      onClick={() => {
                        setIsForgot(false);
                        setError('');
                      }}
                      className="w-full text-sm text-gray-400 hover:text-emerald-300 mt-2"
                      data-testid="cancel-forgot-btn"
                    >
                      Back to Sign In
                    </button>
                  </form>
                )}
              </>
            ) : (
              <>
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-white mb-2">
                {isLogin ? 'Welcome back' : 'Create your account'}
              </h1>
              <p className="text-gray-400">
                {isLogin 
                  ? 'Sign in to continue learning' 
                  : 'Start your learning journey today'}
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-400 text-sm text-center">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <Label htmlFor="name" className="text-gray-400">Full Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      required={!isLogin}
                      className="pl-10 bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-gray-400">Email Address</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="pl-10 bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password" className="text-gray-400">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="pl-10 pr-10 bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {!isLogin && (
                <div>
                  <Label htmlFor="confirmPassword" className="text-gray-400">Confirm Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required={!isLogin}
                      className="pl-10 bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500"
                    />
                  </div>
                </div>
              )}

              {isLogin && (
                <div className="flex items-center justify-end">
                  <button 
                    type="button" 
                    onClick={() => {
                      setIsForgot(true);
                      setError('');
                    }}
                    data-testid="forgot-password-link"
                    className="text-sm text-emerald-400 hover:text-emerald-300"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-5"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />
                    {isLogin ? 'Signing in...' : 'Creating account...'}
                  </div>
                ) : (
                  isLogin ? 'Sign In' : 'Create Account'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-400">
                {isLogin ? "Don't have an account?" : 'Already have an account?'}
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError('');
                    setFormData({ name: '', email: '', password: '', confirmPassword: '' });
                  }}
                  className="ml-2 text-emerald-400 hover:text-emerald-300 font-medium"
                >
                  {isLogin ? 'Sign up' : 'Sign in'}
                </button>
              </p>
            </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Demo Credentials Info */}
        <div className="mt-6 p-4 bg-gray-900/50 border border-gray-800 rounded-lg">
          <p className="text-gray-400 text-sm text-center">
            <span className="text-emerald-400 font-medium">Demo:</span> Create a new account or use any email/password to test
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
